import React, { useEffect, useMemo } from "react";
import {
  RouteObject,
  useLocation,
  useNavigate,
  useRoutes,
} from "react-router-dom";
import { persistor, store } from "./stores";

import { Provider } from "react-redux";
import { PersistGate } from "redux-persist/integration/react";
import { ThemeProvider } from "@mui/system";
import QS from "qs";
import { camelizeKeys } from "humps";
import { merge } from "lodash";
import Layout from "@/screens/Layout";
import AuthenticationLayout from "./screens/authentication";
import SignIn from "./screens/authentication/signin";
import { useForceThemeColorScheme } from "./hooks/useThemeColorScheme";
import SignUp from "./screens/authentication/signup";
import ForgotPassword from "./screens/authentication/forgot-password";
import ResetPassword from "./screens/authentication/reset-password";
import ConfirmEmail from "./screens/authentication/confirm-email";
import Home from "./screens/Home";
import { CssBaseline } from "@mui/material";
import Publication from "./screens/Publication";
import useIsAuthenticated from "./hooks/useIsAuthenticated";

const AppRoute = ({ children }: { children: React.ReactElement }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const hashes = useMemo(() => {
    const h = camelizeKeys(
      QS.parse(location.hash, {
        ignoreQueryPrefix: true,
        delimiter: /[;,&#/?]/,
      })
    );
    const s = camelizeKeys(
      QS.parse(location.search, {
        ignoreQueryPrefix: true,
        delimiter: /[;,&#/?]/,
      })
    );
    return merge(h, s);
  }, [location]) as { [key: string]: any };

  useEffect(() => {
    if (hashes.confirmationToken) {
      navigate(`/authentication/confirm-email/${hashes.confirmationToken}`, {
        replace: true,
      });
    } else if (hashes.recoverToken) {
      navigate(`/authentication/forgot-password/reset/${hashes.recoverToken}`, {
        replace: true,
      });
    }
  }, [hashes, navigate]);

  return children;
};

const RequireAuth = ({ children }: { children: React.ReactElement }) => {
  const navigate = useNavigate();
  const isAuth = useIsAuthenticated();

  useEffect(() => {
    if (!isAuth) {
      navigate(`/authentication/signin`, {
        replace: true,
      });
    }
  }, [isAuth, navigate]);
  return children;
};
// const stripePromise = loadStripe("pk_test_omWCUTVIe8nUXgjO6ZHVMTkW005sGi75SL");

function App() {
  const routes: RouteObject[] = [
    {
      path: "/",
      element: (
        <AppRoute>
          <Layout />
        </AppRoute>
      ),
      children: [
        {
          path: "/",
          index: true,
          element: <Home />,
        },
        {
          path: "/publication/:issueId",
          element: (
            <RequireAuth>
              <Publication />
            </RequireAuth>
          ),
        },
      ],
    },
    {
      path: "/authentication",
      element: (
        <AppRoute>
          <AuthenticationLayout />
        </AppRoute>
      ),
      children: [
        { index: true, element: <SignIn /> },
        { path: "/authentication/signin", element: <SignIn /> },
        {
          path: "/authentication/signin/",
          element: <SignIn />,
          children: [
            { path: "/authentication/signin/:code", element: <SignIn /> },
          ],
        },
        { path: "/authentication/signup", element: <SignUp /> },
        {
          path: "/authentication/signup/",
          element: <SignUp />,
          children: [
            { path: "/authentication/signup/:code", element: <SignUp /> },
          ],
        },
        // { path: "/authentication/signup/:code", element: <SignUp /> },
        {
          path: "/authentication/forgot-password",
          element: <ForgotPassword />,
        },
        {
          path: "/authentication/forgot-password/reset/:token",
          element: <ResetPassword />,
        },
        {
          path: "/authentication/confirm-email/:token",
          element: <ConfirmEmail />,
        },
      ],
    },
  ];
  let element = useRoutes(routes);
  const theme = useForceThemeColorScheme("light");

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline enableColorScheme />

      <Provider store={store}>
        <PersistGate persistor={persistor}>{element}</PersistGate>
      </Provider>
    </ThemeProvider>
  );
}

export default App;
